import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

interface FAQItem {
  question: string;
  answer: string;
}

interface PreviewResult {
  url: string;
  title: string;
  post_id: number;
  post_type: string;
  link?: string;
  faqs: FAQItem[];
  content_updated?: boolean;
  schema_meta_key?: string | null;
  schema_methods_used?: string[];
  schema_status?: string | null;
  seo_plugins_detected?: { rank_math?: boolean; yoast?: boolean };
  operation_id?: number | null;
  verified?: boolean;
}

interface OperationRow {
  id: number;
  project_id: number;
  site_url: string;
  post_url: string;
  post_type: string | null;
  post_id: number;
  post_title: string | null;
  schema_meta_key: string | null;
  schema_methods_used?: string[];
  schema_status?: string | null;
  seo_plugins_detected?: { rank_math?: boolean; yoast?: boolean };
  faqs: FAQItem[];
  status: 'pending' | 'active' | 'failed' | 'reverted';
  error_message: string | null;
  created_at: string | null;
  verified_at: string | null;
  reverted_at: string | null;
}

interface BulkResult {
  url: string;
  title?: string;
  post_id?: number;
  post_type?: string;
  link?: string;
  faqs?: FAQItem[];
  content_updated?: boolean;
  schema_meta_key?: string | null;
  schema_methods_used?: string[];
  schema_status?: string | null;
  error?: string;
}

type Mode = 'single' | 'bulk' | 'audit';

interface AuditRow {
  url: string;
  found: boolean;
  title: string | null;
  post_id: number | null;
  has_bn_block: boolean;
  has_foreign_block: boolean;
  foreign_signals: string[];
  error: string | null;
}

interface AuditReport {
  sitemap_url: string;
  urls_total: number;
  checked: AuditRow[];
  summary: { with_faq: number; without_faq: number; errors: number };
}

export default function FAQInjectorPage() {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = projectIdParam ? parseInt(projectIdParam) : null;

  const [credStatus, setCredStatus] = useState<{ configured: boolean; site_url?: string; username?: string; is_verified?: boolean } | null>(null);
  const [credLoading, setCredLoading] = useState(false);

  const [mode, setMode] = useState<Mode>('single');

  // Single-URL state
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [injecting, setInjecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<PreviewResult | null>(null);

  // Bulk state
  const [bulkUrls, setBulkUrls] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkLog, setBulkLog] = useState<string[]>([]);
  const [bulkResults, setBulkResults] = useState<BulkResult[]>([]);
  // Shared flag: replace FAQs that weren't created by this tool (foreign FAQs).
  const [stripForeign, setStripForeign] = useState(false);

  // Sitemap audit state
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditReport, setAuditReport] = useState<AuditReport | null>(null);

  // History
  const [history, setHistory] = useState<OperationRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertingId, setRevertingId] = useState<number | null>(null);

  const loadHistory = async () => {
    if (!projectId) return;
    try {
      setHistoryLoading(true);
      const r = await api.get(`/faq-injector/history?project_id=${projectId}`);
      setHistory(r.data?.operations || []);
    } catch {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [projectId]);

  const handleRevert = async (op: OperationRow) => {
    if (!projectId) return;
    if (!window.confirm(
      `Revert "${op.post_title || op.post_url}"?\n\n` +
      `This will restore the original post content and remove the FAQ schema. ` +
      `The operation cannot be undone except by re-running the FAQ injection.`
    )) return;
    setRevertingId(op.id);
    setError(null);
    try {
      await api.post(`/faq-injector/revert/${op.id}`);
      await loadHistory();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Revert failed');
    } finally {
      setRevertingId(null);
    }
  };

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [clearingHistory, setClearingHistory] = useState(false);

  const handleDeleteRow = async (op: OperationRow) => {
    if (!projectId) return;
    const isActive = op.status === 'active';
    if (!window.confirm(
      `Delete this history row for "${op.post_title || op.post_url}"?\n\n` +
      (isActive
        ? `⚠️  This row is still ACTIVE — the post on WordPress still has the FAQ block. ` +
          `Deleting only removes the local audit entry; you will lose the ability to revert ` +
          `from this UI. Click Cancel and use Revert instead if you want to undo the inject.\n\n`
        : '') +
      `This only deletes the local history entry — the WordPress post is not touched.`
    )) return;
    setDeletingId(op.id);
    setError(null);
    try {
      await api.delete(`/faq-injector/operations/${op.id}`);
      await loadHistory();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearHistory = async () => {
    if (!projectId || history.length === 0) return;
    const activeCount = history.filter((r) => r.status === 'active').length;
    if (!window.confirm(
      `Clear ALL ${history.length} history rows for this project?\n\n` +
      (activeCount > 0
        ? `⚠️  ${activeCount} row(s) are still ACTIVE — once cleared you will not be able to ` +
          `revert those injections from this UI. The WordPress posts themselves are NOT touched.\n\n`
        : '') +
      `This only deletes the local history entries.`
    )) return;
    setClearingHistory(true);
    setError(null);
    try {
      await api.delete(`/faq-injector/history?project_id=${projectId}`);
      await loadHistory();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Clear failed');
    } finally {
      setClearingHistory(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        setCredLoading(true);
        const r = await api.get(`/faq-injector/credential-status?project_id=${projectId}`);
        if (!cancelled) setCredStatus(r.data);
      } catch (e: any) {
        if (!cancelled) {
          setCredStatus({ configured: false });
        }
      } finally {
        if (!cancelled) setCredLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const canRun = useMemo(
    () => Boolean(projectId && credStatus?.configured),
    [projectId, credStatus],
  );

  const handlePreview = async () => {
    if (!projectId || !url.trim()) return;
    setPreviewing(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await api.post('/faq-injector/preview', {
        project_id: projectId,
        url: url.trim(),
      });
      setPreview(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleInject = async () => {
    if (!projectId || !preview) return;
    setInjecting(true);
    setError(null);
    try {
      const r = await api.post('/faq-injector/inject', {
        project_id: projectId,
        url: preview.url,
        faqs: preview.faqs,
        strip_foreign_faqs: stripForeign,
      });
      setSuccess(r.data);
      setPreview(null);
      loadHistory();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Inject failed');
    } finally {
      setInjecting(false);
    }
  };

  const updateFaq = (i: number, field: keyof FAQItem, val: string) => {
    if (!preview) return;
    const next = preview.faqs.map((f, idx) => (idx === i ? { ...f, [field]: val } : f));
    setPreview({ ...preview, faqs: next });
  };

  const runBulk = async () => {
    if (!projectId) return;
    const urls = bulkUrls
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (urls.length === 0) return;
    setBulkRunning(true);
    setBulkLog([]);
    setBulkResults([]);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token') || '';
      const resp = await fetch('/api/faq-injector/inject-bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          project_id: projectId,
          urls,
          strip_foreign_faqs: stripForeign,
        }),
      });
      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n');
        buf = parts.pop() || '';
        for (const raw of parts) {
          const line = raw.trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (typeof evt.log === 'string') {
              setBulkLog((prev) => [...prev, evt.log]);
            } else if (evt.result) {
              setBulkResults((prev) => [...prev, evt.result]);
            }
          } catch {
            // ignore
          }
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Bulk run failed');
    } finally {
      setBulkRunning(false);
      loadHistory();
    }
  };

  const runAudit = async () => {
    if (!projectId || !sitemapUrl.trim()) return;
    setAuditRunning(true);
    setAuditReport(null);
    setError(null);
    try {
      const r = await api.post('/faq-injector/audit-sitemap', {
        project_id: projectId,
        sitemap_url: sitemapUrl.trim(),
        max_urls: 200,
      });
      setAuditReport(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Sitemap audit failed');
    } finally {
      setAuditRunning(false);
    }
  };

  const queueAuditNoFaqIntoBulk = () => {
    if (!auditReport) return;
    const urls = auditReport.checked
      .filter((r) => !r.error && !r.has_bn_block && !r.has_foreign_block)
      .map((r) => r.url);
    setBulkUrls(urls.join('\n'));
    setMode('bulk');
  };

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl">
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900">FAQ Injector</h1>
            <a
              href="https://drive.google.com/drive/folders/1QuBEqhDAovdSN3YYuuEo2Kf-Q_ckdQ6M"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 border border-brand-200 hover:bg-brand-50 rounded-lg whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Watch tutorial
            </a>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Reads a WordPress post (standard post or any CPT), generates 5 contextual FAQs with Claude,
            appends them to the bottom of the post, and writes FAQ schema (JSON-LD) — only on that post.
            Uses the same WordPress credentials saved under WP Live Update.
          </p>
        </div>

        {!projectId && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-sm">
            Select a project first.
          </div>
        )}

        {projectId && !credLoading && credStatus && !credStatus.configured && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-sm">
            No WordPress credentials saved for this project. Configure them in <strong>WP Live Update</strong> first.
          </div>
        )}

        {credStatus?.configured && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded text-xs">
            WordPress connected: <span className="font-mono">{credStatus.site_url}</span>
            {credStatus.is_verified ? ' · verified' : ''}
          </div>
        )}

        {/* Mode toggle */}
        <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm">
          <button
            onClick={() => setMode('single')}
            className={`px-4 py-1.5 ${mode === 'single' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Single URL
          </button>
          <button
            onClick={() => setMode('bulk')}
            className={`px-4 py-1.5 ${mode === 'bulk' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Bulk URLs
          </button>
          <button
            onClick={() => setMode('audit')}
            className={`px-4 py-1.5 ${mode === 'audit' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Sitemap audit
          </button>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {/* SINGLE-URL flow */}
        {mode === 'single' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">Blog post URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://yoursite.com/blog/the-post-slug/"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={!canRun || previewing || injecting}
                />
                <button
                  onClick={handlePreview}
                  disabled={!canRun || !url.trim() || previewing || injecting}
                  className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 disabled:opacity-50"
                >
                  {previewing ? 'Generating…' : 'Generate FAQs'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                The tool searches every CPT on the site by slug, so any kind of post works.
              </p>
            </div>

            {preview && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">Preview · edit any answer before injecting</div>
                  <div className="font-semibold text-gray-900 mt-0.5">{preview.title}</div>
                  <div className="text-xs text-gray-500 font-mono">post #{preview.post_id} · {preview.post_type}</div>
                </div>
                <div className="space-y-3">
                  {preview.faqs.map((f, i) => (
                    <div key={i} className="border border-gray-200 rounded-md p-3 bg-gray-50/50">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Question {i + 1}</label>
                      <input
                        type="text"
                        value={f.question}
                        onChange={(e) => updateFaq(i, 'question', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                      />
                      <label className="block text-xs font-medium text-gray-600 mt-2 mb-1">Answer</label>
                      <textarea
                        value={f.answer}
                        onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                        rows={3}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setPreview(null)}
                    disabled={injecting}
                    className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Discard
                  </button>
                  <button
                    onClick={handleInject}
                    disabled={injecting}
                    className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
                  >
                    {injecting ? 'Injecting…' : 'Inject into WordPress'}
                  </button>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm space-y-2">
                <div className="font-medium text-emerald-800">
                  ✓ Updated {success.faqs.length} FAQs on "{success.title}"
                </div>
                <div className="text-emerald-700 text-xs space-y-0.5">
                  <div>Post #{success.post_id} · {success.post_type}</div>
                  <div>Content updated: {String(success.content_updated)}</div>
                  <div className="pt-1 border-t border-emerald-200/60 mt-1">
                    <div className="font-medium text-emerald-800">Schema injection</div>
                    <div className="mt-0.5">{success.schema_status || 'Schema added inline in post body'}</div>
                    {success.schema_methods_used && success.schema_methods_used.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {success.schema_methods_used.map((m) => (
                          <span key={m} className="px-1.5 py-0.5 bg-white border border-emerald-300 rounded text-[10px] font-mono">
                            {m}
                          </span>
                        ))}
                      </div>
                    )}
                    {success.seo_plugins_detected && (
                      <div className="mt-1 text-[11px] opacity-80">
                        Plugins detected: {[
                          success.seo_plugins_detected.rank_math ? 'Rank Math' : null,
                          success.seo_plugins_detected.yoast ? 'Yoast' : null,
                        ].filter(Boolean).join(', ') || 'none (Rank Math / Yoast)'}
                      </div>
                    )}
                  </div>
                  {success.link && (
                    <a href={success.link} target="_blank" rel="noopener noreferrer"
                       className="text-emerald-700 underline">View post →</a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BULK flow */}
        {mode === 'bulk' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">URLs (one per line)</label>
              <textarea
                value={bulkUrls}
                onChange={(e) => setBulkUrls(e.target.value)}
                rows={8}
                placeholder={'https://yoursite.com/blog/post-1/\nhttps://yoursite.com/blog/post-2/\nhttps://yoursite.com/programs/program-3/'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                disabled={!canRun || bulkRunning}
              />
              <label className="flex items-start gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={stripForeign}
                  onChange={(e) => setStripForeign(e.target.checked)}
                  disabled={bulkRunning}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">Replace existing FAQs</span> — also strip
                  FAQ sections that weren't created by this tool (manual FAQ headings +
                  foreign FAQPage JSON-LD) before writing. Destructive; off by default.
                </span>
              </label>
              <div className="flex justify-end">
                <button
                  onClick={runBulk}
                  disabled={!canRun || bulkRunning || !bulkUrls.trim()}
                  className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {bulkRunning ? 'Running…' : 'Run bulk inject'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Each URL is processed sequentially. Generation + injection happen in one pass per URL.
              </p>
            </div>

            {(bulkRunning || bulkLog.length > 0) && (
              <div className="bg-gray-900 text-gray-100 text-xs font-mono rounded-md p-3 max-h-72 overflow-y-auto">
                {bulkLog.length === 0 && bulkRunning && <div className="text-gray-400">Connecting…</div>}
                {bulkLog.map((line, i) => (
                  <div key={i} className="whitespace-pre-wrap break-words">{line}</div>
                ))}
              </div>
            )}

            {bulkResults.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">URL</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Post</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">FAQs</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Schema</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResults.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-3 py-2 truncate max-w-[280px]">
                          <a href={r.url} target="_blank" rel="noopener noreferrer"
                             className="text-brand-600 hover:underline">{r.url}</a>
                        </td>
                        <td className="px-3 py-2">
                          {r.title ? (
                            <span className="text-gray-800">
                              {r.title} <span className="text-gray-400 text-xs">#{r.post_id}</span>
                            </span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{r.faqs?.length ?? 0}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.schema_meta_key
                            ? <span className="text-emerald-700 font-mono">{r.schema_meta_key}</span>
                            : <span className="text-gray-500">inline JSON-LD</span>}
                        </td>
                        <td className="px-3 py-2">
                          {r.error
                            ? <span className="px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-xs">error</span>
                            : <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs">done</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* SITEMAP AUDIT flow */}
        {mode === 'audit' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">Sitemap URL</label>
              <input
                type="text"
                value={sitemapUrl}
                onChange={(e) => setSitemapUrl(e.target.value)}
                placeholder="https://yoursite.com/post-sitemap.xml"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                disabled={!canRun || auditRunning}
              />
              <p className="text-xs text-gray-500">
                Accepts a single sitemap or a sitemap-index URL (we recurse one level).
                We check up to 200 URLs per run; each is inspected for either our own
                FAQ block or any manually-authored FAQ section / FAQPage JSON-LD.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={runAudit}
                  disabled={!canRun || auditRunning || !sitemapUrl.trim()}
                  className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {auditRunning ? 'Scanning…' : 'Scan sitemap'}
                </button>
              </div>
            </div>

            {auditReport && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
                  <div className="text-sm text-gray-700">
                    Scanned <span className="font-semibold">{auditReport.urls_total}</span> URLs:
                    <span className="ml-3 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs">
                      {auditReport.summary.with_faq} have FAQs
                    </span>
                    <span className="ml-2 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs">
                      {auditReport.summary.without_faq} missing FAQs
                    </span>
                    {auditReport.summary.errors > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-xs">
                        {auditReport.summary.errors} errors
                      </span>
                    )}
                  </div>
                  <button
                    onClick={queueAuditNoFaqIntoBulk}
                    disabled={auditReport.summary.without_faq === 0}
                    className="px-3 py-1.5 text-sm border border-brand-300 text-brand-700 rounded-lg hover:bg-brand-50 disabled:opacity-50"
                  >
                    Queue {auditReport.summary.without_faq} missing → Bulk inject
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">URL</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Post</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">FAQ status</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditReport.checked.map((r, i) => {
                      const status = r.error
                        ? { label: 'error', cls: 'bg-rose-50 text-rose-700 border-rose-200' }
                        : r.has_bn_block
                          ? { label: 'has BN FAQs', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
                          : r.has_foreign_block
                            ? { label: 'foreign FAQs', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                            : { label: 'no FAQs', cls: 'bg-gray-50 text-gray-700 border-gray-200' };
                      return (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-3 py-2 truncate max-w-[300px]">
                            <a href={r.url} target="_blank" rel="noopener noreferrer"
                               className="text-brand-600 hover:underline">{r.url}</a>
                          </td>
                          <td className="px-3 py-2 truncate max-w-[200px]">
                            {r.title ? (
                              <span className="text-gray-800">
                                {r.title} <span className="text-gray-400 text-xs">#{r.post_id}</span>
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 border rounded-full text-xs ${status.cls}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {r.error
                              ? r.error
                              : r.foreign_signals.length > 0
                                ? r.foreign_signals.join(' · ')
                                : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* History + revert */}
        {projectId && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">History &amp; revert</h2>
                <p className="text-xs text-gray-500">
                  Every injection is logged with the original post content. Revert restores the
                  pre-injection state and clears the FAQ schema for that post.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadHistory}
                  disabled={historyLoading}
                  className="px-3 py-1 text-xs text-gray-700 border border-gray-300 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  {historyLoading ? 'Loading…' : 'Refresh'}
                </button>
                <button
                  onClick={handleClearHistory}
                  disabled={clearingHistory || history.length === 0}
                  title="Delete every history row for this project (does not touch WordPress posts)"
                  className="px-3 py-1 text-xs text-rose-700 border border-rose-200 hover:bg-rose-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {clearingHistory ? 'Clearing…' : 'Clear all'}
                </button>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">When</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Post</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">FAQs</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Schema</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((op) => {
                    const dt = op.created_at ? new Date(op.created_at).toLocaleString() : '—';
                    const status = op.status;
                    const statusCls =
                      status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      status === 'reverted' ? 'bg-slate-50 text-slate-700 border-slate-200' :
                      status === 'failed' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                      'bg-amber-50 text-amber-700 border-amber-200';
                    return (
                      <tr key={op.id} className="border-b border-gray-100">
                        <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{dt}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 truncate max-w-[260px]">
                            {op.post_title || `Post #${op.post_id}`}
                          </div>
                          <a href={op.post_url} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-brand-600 hover:underline truncate inline-block max-w-[260px] align-bottom">
                            {op.post_url}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{(op.faqs || []).length}</td>
                        <td className="px-3 py-2 text-xs" title={op.schema_status || ''}>
                          {(() => {
                            const methods = op.schema_methods_used || [];
                            if (methods.length === 0) {
                              // Legacy rows from before this field — fall back
                              // to schema_meta_key for backward display.
                              return op.schema_meta_key
                                ? <span className="text-emerald-700 font-mono">{op.schema_meta_key}</span>
                                : <span className="text-gray-500">inline JSON-LD</span>;
                            }
                            const hasHead = methods.includes('rank_math_meta') || methods.includes('yoast_faq_block');
                            const label = methods.includes('rank_math_meta')
                              ? 'Rank Math (head)'
                              : methods.includes('yoast_faq_block')
                                ? 'Yoast (head)'
                                : 'inline body only';
                            return (
                              <span className={hasHead ? 'text-emerald-700' : 'text-amber-700'}>
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${statusCls}`}>
                            {status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-2 justify-end">
                            {status === 'active' ? (
                              <button
                                onClick={() => handleRevert(op)}
                                disabled={revertingId === op.id}
                                className="px-3 py-1 text-xs text-rose-700 border border-rose-200 hover:bg-rose-50 rounded disabled:opacity-50"
                              >
                                {revertingId === op.id ? 'Reverting…' : 'Revert'}
                              </button>
                            ) : status === 'reverted' ? (
                              <span className="text-xs text-gray-400">
                                {op.reverted_at ? new Date(op.reverted_at).toLocaleString() : 'reverted'}
                              </span>
                            ) : (
                              <span className="text-xs text-rose-600" title={op.error_message || ''}>
                                {op.error_message ? 'see error' : '—'}
                              </span>
                            )}
                            <button
                              onClick={() => handleDeleteRow(op)}
                              disabled={deletingId === op.id}
                              title="Delete this history row (does not touch WordPress)"
                              className="px-2 py-1 text-xs text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded disabled:opacity-50"
                            >
                              {deletingId === op.id ? '…' : '×'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {history.length === 0 && !historyLoading && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-gray-500 text-sm">
                        No injections recorded yet for this project.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
